import { useState } from "react";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { contactInfo } from "@/mocks/homeData";

const API_BASE = import.meta.env.VITE_API_BASE || "https://api.textileinnovationexchange.com/api";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <section className="bg-tie-grey-light py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 max-w-6xl mx-auto">
          {/* Left: Contact Info */}
          <div>
            <ScrollReveal>
              <div className="mb-4 md:mb-6">
                <img
                  src="https://storage.readdy-site.link/project_files/3ad77496-7e33-4bee-aed3-853667869f76/3a6b7789-d222-4345-bb6d-4232140708c8_TIE1.png?v=90453bddcbe5657cab713355118e545f"
                  alt="TIE Logo"
                  className="h-12 md:h-20 w-auto object-contain"
                />
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / Get in Touch
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                Let's Build Innovation Together
              </h2>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
                Whether you are a factory, brand, researcher, or solution
                provider - TIE is your gateway to systematic textile innovation.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <div className="mt-8 md:mt-10 space-y-4 md:space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-tie-teal/10 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-tie-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-tie-text-primary">
                      Address
                    </p>
                    <p className="text-sm text-tie-text-secondary mt-0.5">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-tie-teal/10 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-tie-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-tie-text-primary">
                      Phone
                    </p>
                    <p className="text-sm text-tie-text-secondary mt-0.5">
                      {contactInfo.phone}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-tie-teal/10 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-tie-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-tie-text-primary">
                      General Inquiries
                    </p>
                    <p className="text-sm text-tie-text-secondary mt-0.5">
                      {contactInfo.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-tie-teal/10 flex items-center justify-center shrink-0">
                    <i className="ri-briefcase-line text-tie-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-tie-text-primary">
                      Partnership & Sales
                    </p>
                    <p className="text-sm text-tie-text-secondary mt-0.5">
                      {contactInfo.partnershipEmail}
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right: Form */}
          <ScrollReveal delay={200}>
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-tie-grey-dark">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-tie-teal/10 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-tie-teal text-2xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-tie-text-primary mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-sm text-tie-text-secondary">
                    Thank you for reaching out. The TIE team will respond
                    shortly.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  method="POST"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const data = new URLSearchParams();
                    data.append("name", formData.name);
                    data.append("email", formData.email);
                    data.append("organization", formData.organization);
                    data.append("message", formData.message);
                    fetch(`${API_BASE}/contacts`, {
                      method: "POST",
                      headers: { "Content-Type": "application/x-www-form-urlencoded" },
                      body: data.toString(),
                    }).then(() => handleSubmit());
                  }}
                >
                  <div className="space-y-4 md:space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-tie-text-primary mb-1.5">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-tie-grey-dark bg-tie-grey-light text-sm text-tie-text-primary focus:outline-none focus:border-tie-teal transition-colors"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-tie-text-primary mb-1.5">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-tie-grey-dark bg-tie-grey-light text-sm text-tie-text-primary focus:outline-none focus:border-tie-teal transition-colors"
                        placeholder="you@organization.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-tie-text-primary mb-1.5">
                        Organization
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-tie-grey-dark bg-tie-grey-light text-sm text-tie-text-primary focus:outline-none focus:border-tie-teal transition-colors"
                        placeholder="Factory, brand, or institution name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-tie-text-primary mb-1.5">
                        Message
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-tie-grey-dark bg-tie-grey-light text-sm text-tie-text-primary focus:outline-none focus:border-tie-teal transition-colors resize-none"
                        placeholder="How would you like to collaborate with TIE?"
                      />
                      <p className="text-xs text-tie-text-muted mt-1 text-right">
                        {formData.message.length}/500
                      </p>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3.5 rounded-lg bg-tie-teal text-white font-medium text-sm hover:bg-tie-teal-dark transition-colors flex items-center justify-center gap-2"
                    >
                      Send Message
                      <i className="ri-send-plane-line" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}